// Employee 360 tabs, modelled on the Stephanet profile (custom/E360Tabs.jsx). Each renderer gets one `ctx`:
// { e, d (sections: documents, salary, reviews, cases, leave, allocations, attendance, idcard, permits, hmo, accommodation),
//   people, canManage, add(resource, defaults), open(resource, row), setTab }
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Plus, X } from 'lucide-react';
import * as hr from '../api';
import StatusPill from '../components/StatusPill';
import MiniTable from '../components/MiniTable';
import { RESOURCES } from '../resources';
import { daysUntil, fmtDate, fmtMoney, initials, monthsSince, t12, titleCase, today } from '../format';
import { hrSubUrl } from '../catalog';
import Icon from '../Icon';
import { benefitRows, outsidePolicy } from '../gradePolicy';
import { BenefitTable } from './AddEmployee';

const sum = (rows, k) => rows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
const serviceText = from => {
  const m = monthsSince(from);
  return m === null ? '—' : `${Math.floor(m / 12)} yr ${m % 12} mo`;
};

const monthsBetween = (from, to) => {
  const a = new Date(`${String(from).slice(0, 10)}T00:00:00`), b = new Date(`${String(to).slice(0, 10)}T00:00:00`);
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth());
};

const TONE_LOOK = { none: ['blue', 'layers'], good: ['green', 'circle-check'], warn: ['amber', 'clock'], bad: ['rose', 'circle-alert'], info: ['purple', 'info'], muted: ['slate', 'minus'] };

function LcTile({ label, value, color, icon, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`lc-tile lc-${color}`} onClick={onClick} type={onClick ? 'button' : undefined}>
      <div className="lc-top"><span className="lc-icon"><Icon name={icon} size={19} /></span><b className="lc-value">{value}</b></div>
      <span className="lc-label">{label}</span>
    </Tag>
  );
}

export function Kpis({ items }) {
  return (
    <div className="lc-grid lc-auto">
      {items.map(([label, value, tone = 'none', icon]) => (
        <LcTile key={label} label={label} value={value} color={TONE_LOOK[tone][0]} icon={icon || TONE_LOOK[tone][1]} />
      ))}
    </div>
  );
}

export function Panel({ title, hint, actions, children }) {
  return (
    <div className="panel">
      <div className="toolbar">
        <div><h3 className="panel-title">{title}</h3>{hint && <p className="muted small tab-hint">{hint}</p>}</div>
        <div className="spacer" />{actions}
      </div>
      {children}
    </div>
  );
}

const AddBtn = ({ ctx, res, label, defaults }) =>
  ctx.canManage ? <button className="btn primary" onClick={() => ctx.add(res, { employee_id: ctx.e.id, ...defaults })}><Plus size={15} /> {label}</button> : null;
const EditLink = ({ ctx, label = 'Edit' }) =>
  ctx.canManage ? <Link className="btn" to={hrSubUrl('add-employee', { id: ctx.e.id })}>{label}</Link> : null;

const Empty = ({ text }) => <div className="empty">{text}</div>;
const yn = v => (v === true ? 'Yes' : v === false ? 'No' : null);
const days = v => (v === null || v === undefined || v === '' ? null : `${v} days`);

export function Tiles({ items }) {
  return (
    <div className="tile-grid">
      {items.map(([l, v]) => <div key={l} className="tile"><span>{l}</span><b>{v === null || v === undefined || v === '' ? '—' : v}</b></div>)}
    </div>
  );
}

// ---------------------------------------------------------------- tabs

function Personal(ctx) {
  const e = ctx.e, p = e.personal_info || {}, pd = e.profile_detail || {};
  return (
    <div className="e360-fields">
      <Panel title="Basic Identity" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['First Name', e.first_name], ['Middle Name', e.middle_name], ['Surname', e.last_name], ['Gender', titleCase(p.gender)],
          ['Date of Birth', fmtDate(p.date_of_birth)], ['Marital Status', titleCase(p.marital_status)], ['Nationality', p.nationality],
          ['State of Origin', p.state_of_origin], ['LGA', p.lga_of_origin], ['Religion', p.religion], ['Blood Group', p.blood_group], ['Genotype', p.genotype]]} />
      </Panel>
      <Panel title="Contact Information" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Work Email', e.work_email], ['Phone Number 1', e.work_phone], ['Phone Number 2', p.personal_phone], ['WhatsApp', pd.whatsapp_number],
          ['Emergency Phone', pd.emergency_phone], ['Preferred Contact', pd.preferred_contact], ['Personal Email', p.personal_email]]} />
      </Panel>
      <Panel title="Address Details" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Current Address', p.residential_address], ['Permanent Address', pd.permanent_address], ['City', pd.city], ['State', pd.state_of_residence],
          ['Country', pd.country], ['Postal Code', pd.postal_code]]} />
      </Panel>
      <Panel title="Means of Identification" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['ID Type', pd.id_type], ['ID Number', pd.id_number], ['Issue Date', fmtDate(pd.id_issue_date)],
          ['Expiry Date', pd.id_expiry_date ? `${fmtDate(pd.id_expiry_date)}${daysUntil(pd.id_expiry_date) < 0 ? ' (expired)' : ''}` : null]]} />
      </Panel>
      <Panel title="Academic & Qualifications" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Highest Qualification', pd.highest_qualification], ['Course of Study', pd.course_of_study], ['Institution', pd.institution],
          ['Year Graduated', pd.graduation_year], ['Professional Certification', pd.professional_certification], ['Skills', pd.skills]]} />
      </Panel>
      <Panel title="Emergency Contacts & Next of Kin" hint={`${e.emergency_contacts.length} contact(s) on file`} actions={<EditLink ctx={ctx} label="Add / Edit" />}>
        {e.emergency_contacts.length ? (
          <div className="table-wrap"><table className="e360-table">
            <thead><tr><th>Name</th><th>Relationship</th><th>Phone</th><th>Alt. Phone</th><th>Email</th><th>Next of Kin</th><th>Primary</th></tr></thead>
            <tbody>{e.emergency_contacts.map(c => (
              <tr key={c.id}><td><b>{c.contact_name}</b></td><td>{c.relationship_type}</td><td>{c.phone_primary}</td><td>{c.phone_secondary || '—'}</td>
                <td>{c.email || '—'}</td><td>{c.is_next_of_kin ? 'Yes' : 'No'}</td><td>{c.is_primary_contact ? 'Yes' : 'No'}</td></tr>
            ))}</tbody>
          </table></div>
        ) : <Empty text="No emergency contacts on file." />}
      </Panel>
      <Panel title="Dependants" hint="Spouses, children and other dependants" actions={<AddBtn ctx={ctx} res="employee_dependents" label="Add Dependant" />}>
        <MiniTable res={RESOURCES.employee_dependents} rows={e.dependents} empty="No dependants on file." onRow={r => ctx.open('employee_dependents', { ...r, employee_name: e.full_name })} />
      </Panel>
    </div>
  );
}

function Employment(ctx) {
  const e = ctx.e, pd = e.profile_detail || {}, w = e.work_rules || {};
  const issued = [['issued_safety_shoe', 'Safety Shoe'], ['issued_coverall', 'Coverall'], ['issued_allied_equipment', 'Allied Equipment'], ['issued_id_card', 'ID Card'],
    ['issued_laptop', 'Laptop'], ['issued_toolkit', 'Toolkit']].filter(([n]) => pd[n]).map(([, l]) => l);
  return (
    <div className="e360-fields">
      <Panel title="Organizational Assignment" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Company', e.company_name], ['Branch', e.branch_name], ['Primary Department', e.department_name],
          ['All Departments', (e.departments || []).map(x => x.name).join(', ')], ['Designation', e.designation_title], ['Job Title', pd.job_title], ['Unit', pd.unit], ['Section', pd.section], ['Team Number', pd.team_number], ['Reporting Office', pd.reporting_office],
          ['Staff Category', pd.staff_category], ['Reports To', e.manager_name]]} />
      </Panel>
      <Panel title="Employment Details" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Employment Type', titleCase(e.employment_type)], ['Status', <StatusPill value={e.employment_status} />], ['Management Type', pd.management_type],
          ['Employee Level', pd.employee_level], ['Permanent Employee', yn(pd.is_permanent)], ['Date of Employment', fmtDate(e.hire_date)], ['Date of Joining', fmtDate(pd.date_of_join)],
          ['Confirmation Date', fmtDate(e.confirmation_date)], ['Exit Date', fmtDate(e.exit_date)], ['Total Service', serviceText(e.hire_date)], ['Private Record', e.is_private ? 'Yes' : 'No']]} />
      </Panel>
      <Panel title="Operational Assignment" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Primary Project Site', e.site_name], ['Company Site', pd.company_site], ['Site Access Level', pd.site_access_level],
          ['Head Office Based', yn(pd.head_office_based)], ['Covers All Sites', yn(pd.cover_all_sites)]]} />
      </Panel>
      <Panel title="Work Rules & Schedule" hint={w.work_rules_source === 'manual' ? `Manual setup — ${w.work_rules_override_reason || 'no reason recorded'}` : 'Follows the salary grade policy'} actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Shift', w.shift_name], ['Shift Type', w.shift_type], ['Work Pattern', w.work_pattern], ['Work Days', w.work_days],
          ['Days / Week', w.days_per_week], ['Daily Hours', w.daily_hours !== null && w.daily_hours !== undefined ? Number(w.daily_hours) : null],
          ['Shift Hours', w.shift_start && w.shift_end ? `${w.shift_start}–${w.shift_end}` : null], ['Attendance Capture', w.attendance_method]]} />
      </Panel>
      <Panel title="Leave Entitlements" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Annual Leave', days(w.annual_leave_days)], ['Sick Leave', days(w.sick_leave_days)], ['Casual Leave', days(w.casual_leave_days)],
          ['Maternity Leave', days(w.maternity_leave_days)], ['Paternity Leave', days(w.paternity_leave_days)], ['Compassionate Leave', days(w.compassionate_leave_days)],
          ['Carry Forward', w.carry_forward_allowed ? `Yes${w.max_carry_forward_days ? ` (max ${w.max_carry_forward_days} days)` : ''}` : yn(w.carry_forward_allowed)],
          ['Accrual', w.leave_accrual_method]]} />
      </Panel>
      <Panel title="PPE / Work Gear Issued" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Items Issued', issued.length ? issued.join(', ') : 'None recorded'], ['PPE / Boot Size', pd.ppe_size], ['Uniform Size', pd.uniform_size],
          ['Date Issued', fmtDate(pd.ppe_issue_date)], ['Issued By', pd.ppe_issued_by], ['Remarks', pd.ppe_remarks]]} />
      </Panel>
    </div>
  );
}

function Reporting(ctx) {
  const e = ctx.e;
  const manager = ctx.people.find(p => p.id === e.reports_to_id);
  const reports = ctx.people.filter(p => p.reports_to_id === e.id);
  const rows = [
    { role: 'Line Manager / Supervisor', person: manager || (e.manager_name ? { full_name: e.manager_name } : null) },
    ...reports.map(p => ({ role: 'Direct Report', person: p })),
  ];
  return (
    <Panel title="Map of Reporting Line" hint="Reporting structure, approver mapping & escalation chain" actions={<EditLink ctx={ctx} label="Edit Mapping" />}>
      <div className="mapping-status">
        <span className={`ref-tag ${manager ? 'done' : 'req'}`}>Mapping Status: {manager ? 'Complete' : 'Incomplete'}</span>
        <span className="muted small">{manager ? 'Line manager assigned' : 'No line manager assigned'} · {reports.length} direct report(s)</span>
      </div>
      <div className="table-wrap">
        <table className="e360-table">
          <thead><tr><th>Role</th><th>Assigned Person</th><th>Position</th><th>Department</th><th>Employee ID</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><b>{r.role}</b></td>
                <td>{r.person ? <span className="emp-cell"><span className="emp-avatar">{initials(r.person.full_name)}</span>
                  {r.person.id ? <Link to={hrSubUrl('employee-360', { id: r.person.id })}>{r.person.full_name}</Link> : r.person.full_name}</span> : <span className="muted">Not assigned</span>}</td>
                <td>{r.person?.designation_title || '—'}</td><td>{r.person?.department_name || '—'}</td><td>{r.person?.employee_no || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Sites(ctx) {
  const e = ctx.e;
  const permits = ctx.d.permits;
  return (
    <>
      <Panel title="Primary Project Site Access" hint="Main site assignment" actions={<EditLink ctx={ctx} label="Edit Primary" />}>
        <Tiles items={[['Primary Project Site', e.site_name], ['Branch', e.branch_name], ['Company', e.company_name], ['Assigned Since', fmtDate(e.hire_date)],
          ['Access Status', titleCase(e.employment_status)], ['Additional Sites', permits.filter(p => p.status === 'active').length]]} />
      </Panel>
      <Panel title="Other Assigned Sites" hint="Additional project sites this employee may work on — HSE induction, medical and PPE clearance per site"
             actions={<AddBtn ctx={ctx} res="site_accesses" label="Grant Site Access" />}>
        {permits.length ? (
          <>
            <div className="site-chips">{permits.map(p => <span key={p.id} className={`site-chip ${p.status !== 'active' ? 'off' : ''}`}>{p.site_name || 'Site'}</span>)}</div>
            <div className="tab-files"><MiniTable res={RESOURCES.site_accesses} rows={permits} columns={['site_id', 'access_type', 'start_date', 'end_date', 'hse_induction_passed', 'medical_fitness_passed', 'ppe_issued', 'status']} /></div>
          </>
        ) : <Empty text="No other sites assigned." />}
      </Panel>
    </>
  );
}

function Probation(ctx) {
  const e = ctx.e, pd = e.profile_detail || {};
  const left = e.probation_end_date ? daysUntil(e.probation_end_date) : null;
  const on = e.employment_status === 'probation';
  return (
    <>
    <Panel title="Probation Details"
           hint={on ? (left !== null && left < 0 ? `Probation ended ${-left} day(s) ago — confirmation overdue` : `On probation${left !== null ? ` — ${left} day(s) left` : ''}`) : 'Not currently on probation'}
           actions={<EditLink ctx={ctx} />}>
      <Tiles items={[['On Probation?', on ? 'Yes' : 'No'], ['Start Date', fmtDate(pd.probation_start_date || e.hire_date)], ['End Date', fmtDate(e.probation_end_date)],
        ['Duration', pd.probation_months ? `${pd.probation_months} Months` : e.probation_end_date && e.hire_date ? `${monthsBetween(pd.probation_start_date || e.hire_date, e.probation_end_date)} Months` : null],
        ['Probation Department', pd.probation_department_name], ['Dept After Probation', pd.post_probation_department_name], ['Current Designation', e.designation_title],
        ['Reviewer', pd.probation_reviewer_name], ['Confirmation Due', fmtDate(pd.confirmation_due_date)], ['Confirmation Date', fmtDate(e.confirmation_date)], ['Status', titleCase(e.employment_status)]]} />
    </Panel>
    <Panel title="Identification Documents" hint="Government-issued ID on file">
      <Tiles items={[['ID Type', pd.id_type], ['ID Number', pd.id_number], ['Issue Date', fmtDate(pd.id_issue_date)],
        ['Expiry Date', pd.id_expiry_date ? `${fmtDate(pd.id_expiry_date)}${daysUntil(pd.id_expiry_date) < 0 ? ' (expired)' : ''}` : null]]} />
    </Panel>
    </>
  );
}

// every dated credential: documents on file plus the expatriate passport / visa / CERPAC
export function expiringItems(e, docs) {
  const x = e.expatriate_detail || {};
  const items = docs.map(d => ({ name: d.title || titleCase(d.document_type), type: titleCase(d.document_type), number: d.document_number, issue: d.issue_date, expiry: d.expiry_date }));
  if (x.passport_expiry_date) items.push({ name: 'Passport', type: 'Passport', number: x.passport_number, expiry: x.passport_expiry_date });
  if (x.visa_expiry_date) items.push({ name: `Visa${x.visa_type ? ` (${x.visa_type})` : ''}`, type: 'Visa', expiry: x.visa_expiry_date });
  if (x.cerpac_expiry_date) items.push({ name: 'CERPAC', type: 'CERPAC', number: x.cerpac_number, expiry: x.cerpac_expiry_date });
  const pd = e.profile_detail || {};
  if (pd.id_expiry_date) items.push({ name: pd.id_type || 'ID Document', type: 'Identification', number: pd.id_number, issue: pd.id_issue_date, expiry: pd.id_expiry_date });
  return items.map(d => ({ ...d, left: d.expiry ? daysUntil(d.expiry) : null }));
}

function Expiry(ctx) {
  const withDays = expiringItems(ctx.e, ctx.d.documents);
  const within = n => withDays.filter(d => d.left !== null && d.left >= 0 && d.left <= n).length;
  return (
    <Panel title="Employee Document Expiry Tracker" hint="Compliance documents, licences and permits with expiry tracking"
           actions={<AddBtn ctx={ctx} res="employee_documents" label="Add Document" />}>
      <Kpis items={[['Total Tracked', withDays.length], ['Active', withDays.filter(d => d.left === null || d.left > 90).length, 'good'],
        ['Expired', withDays.filter(d => d.left !== null && d.left < 0).length, 'bad'], ['Exp. in 30 Days', within(30), 'warn'],
        ['Exp. in 60 Days', within(60), 'warn'], ['Exp. in 90 Days', within(90), 'info']]} />
      {withDays.length ? (
        <div className="table-wrap"><table className="e360-table">
          <thead><tr><th>Document</th><th>Type</th><th>Number</th><th>Issue Date</th><th>Expiry Date</th><th>Days Left</th><th>Status</th></tr></thead>
          <tbody>{[...withDays].sort((a, b) => (a.left ?? 1e9) - (b.left ?? 1e9)).map((d, i) => (
            <tr key={i}><td><b>{d.name}</b></td><td>{d.type}</td><td>{d.number || '—'}</td><td>{fmtDate(d.issue)}</td><td>{fmtDate(d.expiry)}</td>
              <td>{d.left === null ? '—' : d.left < 0 ? `${-d.left} overdue` : d.left}</td>
              <td><span className={`pill tone-${d.left === null ? 'muted' : d.left < 0 ? 'bad' : d.left <= 30 ? 'warn' : d.left <= 90 ? 'info' : 'good'}`}>
                {d.left === null ? 'No Expiry' : d.left < 0 ? 'Expired' : d.left <= 30 ? 'Expiring Soon' : d.left <= 90 ? `In ${d.left} days` : 'Active'}</span></td></tr>
          ))}</tbody>
        </table></div>
      ) : <Empty text="No documents with expiry dates on file." />}
    </Panel>
  );
}

// a component (it loads the grade policy); the tabs are called as plain functions
const Financial = ctx => <FinancialView {...ctx} />;
function FinancialView(ctx) {
  const e = ctx.e, b = e.statutory_and_bank || {}, c = e.compensation || {}, pd = e.profile_detail || {};
  const money = v => (v === null || v === undefined ? null : fmtMoney(v));
  const pct = v => (v === null || v === undefined ? null : `${Number(v)}%`);
  const [policy, setPolicy] = useState(null);   // Payroll › Grade Policy Setup
  useEffect(() => { hr.gradeSetup().then(r => setPolicy(Array.isArray(r?.families) ? r : null)).catch(() => setPolicy(null)); }, []);
  return (
    <div className="e360-fields">
      <Panel title="Salary Configuration" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Salary Type', titleCase(e.salary_type)], ['Salary Mode', c.salary_mode === 'manual' ? 'Manual configuration' : c.salary_mode ? 'Salary grade' : null],
          ['Employee Category', c.employee_category], ['Job Family', c.job_family], ['Position', c.job_position], ['Salary Grade / Pay Scale', c.pay_grade],
          ...(c.salary_mode === 'manual' || c.grade_exception_reason ? [['Manual Setup / Grade Exception Reason', c.grade_exception_reason]] : [])]} />
        {outsidePolicy(policy, c) && <div className="alert warn" style={{ marginTop: 8 }}>{outsidePolicy(policy, c)}.</div>}
      </Panel>
      {policy && policy.targets.length > 0 && (
        <Panel title="Benefit Eligibility">
          <BenefitTable rows={benefitRows(policy, c)} />
        </Panel>
      )}
      <Panel title="Salary Amounts" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Base Salary (monthly)', fmtMoney(e.basic_salary)], ...(e.salary_type === 'daily' ? [['Daily Rate', money(c.daily_rate)]] : []),
          ['Total Allowances', money(e.total_allowances)], ['Monthly Gross', money(e.monthly_gross)], ['Annual Gross', fmtMoney((Number(e.monthly_gross) || 0) * 12)],
          ['On Review', c.salary_on_review ? `Yes${c.next_review_date ? ` — ${fmtDate(c.next_review_date)}` : ''}` : yn(c.salary_on_review)]]} />
      </Panel>
      <Panel title="Allowances" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Housing', money(c.housing_allowance)], ['Transport', money(c.transport_allowance)], ['Meal', money(c.meal_allowance)],
          ['Utility', money(c.utility_allowance)], ['Medical', money(c.medical_allowance)], ['Other', money(c.other_allowance)]]} />
      </Panel>
      <Panel title="Overtime, Bonus, Pension & Tax" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Overtime Eligible', yn(c.overtime_eligible)], ['Overtime Rate', c.overtime_rate ? `${Number(c.overtime_rate)}×` : null],
          ['Overtime After', c.overtime_threshold_hours ? `${Number(c.overtime_threshold_hours)} h/day` : null], ['Bonus Eligible', yn(c.bonus_eligible)],
          ['Leave Allowance', yn(c.leave_allowance_eligible)], ['Include in Pension', yn(c.include_in_pension)], ['Employee Pension', pct(c.pension_employee_rate)],
          ['Employer Pension', pct(c.pension_employer_rate)], ['Include in PAYE', yn(c.include_in_paye)]]} />
      </Panel>
      <Panel title="Payment Details" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Payment Method', c.payment_method], ['Bank Name', b.bank_name], ['Account Name', b.bank_account_name], ['Account Number', b.bank_account_number],
          ['Bank Branch', c.bank_branch], ['Sort Code', b.bank_sort_code], ['BVN', b.bvn]]} />
      </Panel>
      <Panel title="Pension & Statutory Numbers" actions={<EditLink ctx={ctx} />}>
        <Tiles items={[['Pension Provider / PFA', b.pension_fund_administrator], ['Pension ID / RSA Number', b.pension_rsa_pin],
          ['Tax Identification Number (TIN)', b.tax_identification_number], ['NHF Number', b.nhf_number], ['NHIA Number', c.nhia_number],
          ['NSITF Number', c.nsitf_number], ['ITF Number', c.itf_number], ['HMO Provider', c.hmo_provider], ['HMO ID', c.hmo_id]]} />
      </Panel>
      {(pd.hr_notes || pd.onboarding_remark || pd.payroll_remark) && (
        <Panel title="Internal HR Notes" actions={<EditLink ctx={ctx} />}>
          <Tiles items={[['HR Notes', pd.hr_notes], ['Onboarding Remark', pd.onboarding_remark], ['Payroll Remark', pd.payroll_remark]]} />
        </Panel>
      )}
    </div>
  );
}

function References(ctx) {
  const refs = ctx.e.references || [];
  const setStatus = async (r, status) => {
    try { await hr.setReferenceStatus(r.id, status); ctx.notify(`Reference ${r.position} marked ${status}`); ctx.reload(); }
    catch (err) { ctx.notify(err.message, 'bad'); }
  };
  const verified = refs.filter(r => r.status === 'verified').length;
  return (
    <Panel title="Employee References / Guarantors" hint={`${refs.length} reference(s) · ${verified} verified${refs.length > verified ? ' · verification pending' : ''}`}
           actions={<EditLink ctx={ctx} label="Add / Edit References" />}>
      <Kpis items={[['Total', refs.length], ['Verified', verified, 'good'], ['Pending', refs.filter(r => r.status === 'pending').length, 'warn'],
        ['Rejected', refs.filter(r => r.status === 'rejected').length, 'bad']]} />
      {refs.length ? (
        <div className="table-wrap">
          <table className="e360-table">
            <thead><tr><th>#</th><th>Full Name</th><th>Relationship</th><th>Company</th><th>Phone</th><th>Email</th><th>Address</th><th>Status</th>{ctx.canManage && <th>Actions</th>}</tr></thead>
            <tbody>
              {refs.map(r => (
                <tr key={r.id}>
                  <td>{r.position}</td><td><b>{r.full_name}</b></td><td>{r.relationship_type || '—'}</td><td>{r.company || '—'}</td><td>{r.phone || '—'}</td>
                  <td>{r.email || '—'}</td><td className="wrap">{r.address || '—'}</td>
                  <td><span className={`pill tone-${{ verified: 'good', rejected: 'bad' }[r.status] || 'warn'}`}>{r.status === 'pending' ? 'Pending Verification' : titleCase(r.status)}</span></td>
                  {ctx.canManage && (
                    <td><div className="row-actions">
                      {r.status !== 'verified' && <button className="btn small" onClick={() => setStatus(r, 'verified')}><Check size={13} /> Verify</button>}
                      {r.status !== 'rejected' && <button className="btn small ghost" onClick={() => setStatus(r, 'rejected')}><X size={13} /> Reject</button>}
                    </div></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty text="No references on file." />}
    </Panel>
  );
}

function SalaryHistory(ctx) {
  const rows = ctx.d.salary;
  return (
    <Panel title={`Salary History — ${ctx.e.full_name}`} hint="Salary changes, progression and compensation updates. Recording a change updates the employee's basic salary."
           actions={<AddBtn ctx={ctx} res="salary_changes" label="Add Change" defaults={{ effective_date: today() }} />}>
      {rows.length ? (
        <div className="table-wrap"><table className="e360-table">
          <thead><tr><th>Effective Date</th><th>Old Salary</th><th>New Salary</th><th>Change</th><th>% Change</th><th>Type</th><th>Reason</th></tr></thead>
          <tbody>{rows.map(c => {
            const diff = (Number(c.new_basic_salary) || 0) - (Number(c.previous_basic_salary) || 0);
            return (
              <tr key={c.id}><td>{fmtDate(c.effective_date)}</td>
                <td className="num">{fmtMoney(c.previous_basic_salary)}</td><td className="num">{fmtMoney(c.new_basic_salary)}</td>
                <td className={`num ${diff < 0 ? 'neg' : ''}`}>{`${diff >= 0 ? '+' : ''}${fmtMoney(diff)}`}</td>
                <td>{c.percentage_change !== null && c.percentage_change !== undefined ? `${Number(c.percentage_change).toFixed(1)}%` : '—'}</td>
                <td>{titleCase(c.change_type)}</td><td className="wrap">{c.reason}</td></tr>
            );
          })}</tbody>
        </table></div>
      ) : <Empty text="No salary changes recorded yet. Use Add Change to record a raise, promotion or correction." />}
    </Panel>
  );
}

function Leave(ctx) {
  const { allocations, leave } = ctx.d;
  const approved = leave.filter(r => r.status === 'approved');
  return (
    <>
      <Kpis items={[['Approved Days', sum(approved, 'days_requested'), 'good'], ['Pending Requests', leave.filter(r => ['pending', 'pre_approved'].includes(r.status)).length, 'warn'],
        ['Requests', leave.length, 'info'], ['Leave Types Tracked', allocations.length]]} />
      <Panel title={`Leave Balances — ${new Date().getFullYear()}`} hint="Entitlement, carry-forward, adjustments and days taken per leave type">
        {allocations.length ? (
          <div className="table-wrap"><table className="e360-table">
            <thead><tr><th>Leave Type</th><th>Entitled</th><th>Carried Forward</th><th>Adjusted</th><th>Taken</th><th>Monetized</th><th>Pending</th><th>Remaining</th></tr></thead>
            <tbody>{allocations.map(a => (
              <tr key={a.leave_type}><td><b>{a.leave_type}</b></td><td className="num">{Number(a.entitled)}</td><td className="num">{Number(a.carried)}</td>
                <td className="num">{Number(a.adjusted)}</td><td className="num">{Number(a.used)}</td><td className="num">{Number(a.monetized)}</td>
                <td className="num">{Number(a.pending)}</td><td className="num"><b>{Number(a.remaining)}</b></td></tr>
            ))}</tbody>
          </table></div>
        ) : <Empty text="No tracked leave types apply to this employee." />}
      </Panel>
      <Panel title="Leave Requests" actions={<AddBtn ctx={ctx} res="leave_requests" label="Add Leave" />}>
        <MiniTable res={RESOURCES.leave_requests} rows={leave} onRow={r => ctx.open('leave_requests', r)} empty="No leave requests yet." />
      </Panel>
    </>
  );
}

function Attendance(ctx) {
  const rows = ctx.d.attendance;
  return (
    <>
      <Kpis items={[['Present', rows.filter(r => r.status === 'present').length, 'good'], ['Absent', rows.filter(r => r.status === 'absent').length, 'bad'],
        ['Late Arrivals', rows.filter(r => r.is_late).length, 'warn'], ['Overtime Hours', sum(rows, 'overtime_hours').toFixed(1), 'info'], ['Records Shown', rows.length]]} />
      <Panel title="Attendance — last 90 days" hint="Daily register entries for this employee">
        {rows.length ? (
          <div className="table-wrap"><table className="e360-table">
            <thead><tr><th>Date</th><th>Time In</th><th>Time Out</th><th>Hours</th><th>Overtime</th><th>Late</th><th>Status</th><th>Remarks</th></tr></thead>
            <tbody>{[...rows].reverse().map(r => (
              <tr key={r.id}><td>{fmtDate(r.work_date)}</td><td>{t12(r.time_in) || '—'}</td><td>{t12(r.time_out) || '—'}</td><td className="num">{Number(r.hours_worked) || '—'}</td>
                <td className="num">{Number(r.overtime_hours) || '—'}</td><td>{r.is_late ? 'Yes' : '—'}</td><td><StatusPill value={r.status} /></td><td className="wrap">{r.reason || '—'}</td></tr>
            ))}</tbody>
          </table></div>
        ) : <Empty text="No attendance recorded in the last 90 days." />}
      </Panel>
    </>
  );
}

function Documents(ctx) {
  const docs = ctx.d.documents.map(d => ({ ...d, employee_name: ctx.e.full_name, employee_no: ctx.e.employee_no }));
  return (
    <>
      <Kpis items={[['Documents', docs.length], ['Verified', docs.filter(d => d.is_verified).length, 'good'], ['Awaiting Verification', docs.filter(d => !d.is_verified).length, 'warn'],
        ['Expired', docs.filter(d => d.is_expired).length, 'bad']]} />
      <Panel title="Documents" hint="Passports, permits, certificates and signed forms on file" actions={<AddBtn ctx={ctx} res="employee_documents" label="Add Document" />}>
        <MiniTable res={RESOURCES.employee_documents} rows={docs} columns={['document_type', 'title', 'document_number', 'issue_date', 'expiry_date', 'expiry_state', 'is_verified', 'scan']}
                   onRow={r => ctx.open('employee_documents', r)} empty="No documents on file." />
      </Panel>
    </>
  );
}

function Performance(ctx) {
  const rows = ctx.d.reviews;
  const last = rows[0];
  const parts = [['KPI', 'kpi_score'], ['Attendance', 'attendance_score'], ['Technical', 'technical_score'], ['Compliance', 'compliance_score'], ['Behaviour', 'behaviour_score']];
  return (
    <>
      {last && (
        <div className="panel review-latest">
          <div className="score-ring"><b>{last.overall_score !== null && last.overall_score !== undefined ? Number(last.overall_score).toFixed(0) : '—'}</b><span>{last.rating || 'Not rated'}</span></div>
          <div className="grow">
            <h3 className="panel-title">Latest Review — {last.review_period}</h3>
            <p className="muted small">{last.reviewer_name || 'No reviewer'} · {fmtDate(last.review_date)}</p>
            <div className="score-bars">
              {parts.map(([l, k]) => <div key={k}><span>{l}</span><div className="bar"><i style={{ width: `${Math.min(100, Number(last[k]) || 0)}%` }} /></div><b>{last[k] !== null && last[k] !== undefined ? Number(last[k]) : '—'}</b></div>)}
            </div>
            <p className="small">Merit <b>{last.recommended_merit_increase_pct ?? 0}%</b>{last.recommended_promotion ? ' · Recommended for promotion' : ''}</p>
          </div>
        </div>
      )}
      <Panel title="Performance Review History" hint="Scored reviews; the overall score and rating are worked out from the five scores"
             actions={<AddBtn ctx={ctx} res="performance_reviews" label="Add Review" defaults={{ review_date: today(), reviewer_id: ctx.e.reports_to_id }} />}>
        <MiniTable res={RESOURCES.performance_reviews} rows={rows} onRow={r => ctx.open('performance_reviews', r)} empty="No performance reviews yet." />
      </Panel>
    </>
  );
}

function Timeline({ events }) {
  return (
    <div className="timeline">
      {events.map((ev, i) => (
        <div key={i} className="tl-item">
          <span className={`tl-dot tone-${ev.tone || 'info'}`} />
          <div><b>{ev.title}</b> {ev.status && <StatusPill value={ev.status} />}<small>{ev.when || '—'}{ev.meta ? ` · ${ev.meta}` : ''}</small>{ev.text && <p>{ev.text}</p>}</div>
        </div>
      ))}
    </div>
  );
}

const MOVES = ['transfer', 'outstation'];
const moveMeta = c => [[c.from_department_name, c.to_department_name], [c.from_site_name, c.to_site_name], [c.from_designation_title, c.to_designation_title]]
  .filter(([a, b]) => b && a !== b).map(([a, b]) => `${a || '—'} → ${b}`).join(' · ');

function JobHistory(ctx) {
  const e = ctx.e;
  const cases = ctx.d.cases.filter(c => c.case_type !== 'disciplinary');
  const changes = ctx.d.salary;
  const events = [
    ...cases.map(c => ({ title: `${titleCase(c.case_type)}: ${c.title}`, when: fmtDate(c.effective_date), status: c.status, meta: moveMeta(c), text: c.details, tone: c.case_type === 'promotion' ? 'good' : 'info', sort: c.effective_date })),
    ...changes.map(c => ({ title: `${titleCase(c.change_type)} — ${fmtMoney(c.new_basic_salary)}`, when: fmtDate(c.effective_date), meta: c.reason, tone: 'good', sort: c.effective_date })),
    { title: `Hired — ${e.designation_title || 'Employee'}`, when: fmtDate(e.hire_date), meta: [e.department_name, e.site_name].filter(Boolean).join(' · '), tone: 'good', sort: e.hire_date || '0' },
  ].sort((a, b) => String(b.sort || '').localeCompare(String(a.sort || '')));
  return (
    <>
      <Kpis items={[['Career Events', events.length], ['Promotions', cases.filter(c => c.case_type === 'promotion').length, 'good'],
        ['Transfers', cases.filter(c => MOVES.includes(c.case_type)).length, 'info'], ['Pay Changes', changes.length]]} />
      <Panel title="Employment Timeline" hint="Hire, promotions, transfers and recorded pay changes"><Timeline events={events} /></Panel>
    </>
  );
}

function WorkHistory(ctx) {
  const e = ctx.e;
  const moves = ctx.d.cases.filter(c => MOVES.includes(c.case_type));
  const rows = [
    { period: `${fmtDate(e.hire_date)} → present`, role: e.designation_title, dept: e.department_name, site: e.site_name, type: titleCase(e.employment_type), status: e.employment_status },
    ...moves.map(m => ({ period: fmtDate(m.effective_date), role: m.title, dept: m.to_department_name, site: [m.from_site_name, m.to_site_name].filter(Boolean).join(' → '), type: titleCase(m.case_type), status: m.status })),
  ];
  const sites = new Set([e.site_name, ...moves.map(m => m.to_site_name)].filter(Boolean));
  const depts = new Set([e.department_name, ...moves.map(m => m.to_department_name)].filter(Boolean));
  return (
    <>
      <Kpis items={[['Work Assignments', rows.length], ['Departments Worked', depts.size], ['Project / Site Postings', sites.size, 'info'], ['Total Service', serviceText(e.hire_date), 'good']]} />
      <Panel title="Work History Register" hint="Internal assignments, departments, sites and postings across this employee's service">
        <div className="table-wrap"><table className="e360-table">
          <thead><tr><th>Period</th><th>Role / Position</th><th>Department</th><th>Site</th><th>Type</th><th>Status</th></tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i}><td>{r.period}</td><td><b>{r.role || '—'}</b></td><td>{r.dept || '—'}</td><td>{r.site || '—'}</td><td>{r.type || '—'}</td><td><StatusPill value={r.status} /></td></tr>)}</tbody>
        </table></div>
      </Panel>
    </>
  );
}

function Queries(ctx) {
  const rows = ctx.d.cases.filter(c => c.case_type === 'disciplinary');
  return (
    <>
      <Kpis items={[['Total Queries', rows.length], ['Closed / Resolved', rows.filter(r => r.status === 'approved').length, 'good'],
        ['Pending / Open', rows.filter(r => ['pending', 'pre_approved'].includes(r.status)).length, 'warn'], ['Rejected', rows.filter(r => r.status === 'rejected').length, 'muted']]} />
      <Panel title="Employee Queries" hint="Queries, warnings and disciplinary cases"
             actions={<AddBtn ctx={ctx} res="hr_cases" label="Raise Query" defaults={{ case_type: 'disciplinary', effective_date: today() }} />}>
        <MiniTable res={RESOURCES.hr_cases} rows={rows} columns={['case_number', 'title', 'effective_date', 'status']} onRow={r => ctx.open('hr_cases', r)} empty="No queries on record." />
      </Panel>
    </>
  );
}

function AccommodationTab(ctx) {
  const rows = ctx.d.accommodation;
  return (
    <>
      <Kpis items={[['Housing Support Records', rows.filter(r => r.support_type === 'housing_support').length], ['Company Accommodation', rows.filter(r => r.support_type === 'company_accommodation').length, 'info'],
        ['Active Records', rows.filter(r => r.status === 'active').length, 'good'], ['Ending in 60 Days', rows.filter(r => r.status === 'active' && r.end_date && daysUntil(r.end_date) <= 60).length, 'warn']]} />
      <Panel title="Accommodation" hint="Housing support and company accommodation"
             actions={<AddBtn ctx={ctx} res="accommodation_assignments" label="Add Accommodation" defaults={{ support_type: 'company_accommodation', start_date: today() }} />}>
        <MiniTable res={RESOURCES.accommodation_assignments} rows={rows} columns={['accommodation_id', 'support_type', 'unit_or_room', 'monthly_amount', 'start_date', 'end_date', 'status']}
                   onRow={r => ctx.open('accommodation_assignments', r)} empty="No housing support or accommodation records." />
      </Panel>
    </>
  );
}

function IdCards(ctx) {
  const c = ctx.d.idcard;
  const hmo = ctx.d.hmo;
  return (
    <>
      <Panel title="ID Card" hint="Corporate identity card and badge status"
             actions={!c && <AddBtn ctx={ctx} res="id_cards" label="Request ID Card" />}>
        {c ? (
          <>
            <Tiles items={[['Card Number', c.card_number], ['Template', titleCase(c.template_type)], ['Issue Date', fmtDate(c.issue_date)], ['Expiry Date', fmtDate(c.expiry_date)],
              ['Status', <StatusPill value={c.status} />], ['Barcode / QR', c.barcode_qr]]} />
            <div className="tab-files"><button className="btn" onClick={() => ctx.open('id_cards', { ...c, employee_name: ctx.e.full_name })}>Manage card →</button></div>
          </>
        ) : <Empty text="No ID card requested yet." />}
      </Panel>
      <Panel title="HMO" hint="Health insurance enrollment" actions={!hmo.length && <AddBtn ctx={ctx} res="hmo_enrollments" label="Enroll in HMO" />}>
        <MiniTable res={RESOURCES.hmo_enrollments} rows={hmo} columns={['provider_name', 'plan_tier', 'policy_number', 'dependents_count', 'monthly_premium', 'status']} empty="Not enrolled in an HMO." />
      </Panel>
    </>
  );
}

// key -> [label, icon, renderer, count(ctx)]
export const TABS = {
  personal: ['Personal Details', 'book-open', Personal],
  employment: ['Employment', 'briefcase', Employment],
  reporting: ['Map of Reporting Line', 'users', Reporting],
  sites: ['Project Sites Assigned Access', 'map-pin', Sites, ctx => ctx.d.permits.length],
  probation: ['Probation & Doc Details', 'circle-check', Probation],
  references: ['Employee References', 'user-check', References, ctx => (ctx.e.references || []).length],
  expiry: ['Document Expiry', 'triangle-alert', Expiry, ctx => expiringItems(ctx.e, ctx.d.documents).filter(x => x.expiry).length],
  financial: ['Financial Details', 'dollar-sign', Financial],
  salary: ['Salary History', 'trending-up', SalaryHistory, ctx => ctx.d.salary.length],
  leave: ['Leave', 'calendar', Leave, ctx => ctx.d.leave.length],
  attendance: ['Attendance', 'clock', Attendance, ctx => ctx.d.attendance.length],
  documents: ['Documents', 'file-text', Documents, ctx => ctx.d.documents.length],
  performance: ['Performance', 'star', Performance, ctx => ctx.d.reviews.length],
  jobs: ['Job History', 'history', JobHistory],
  work: ['Work History', 'briefcase-business', WorkHistory],
  queries: ['Queries', 'message-square-warning', Queries, ctx => ctx.d.cases.filter(c => c.case_type === 'disciplinary').length],
  accommodation: ['Accommodation', 'building-2', AccommodationTab, ctx => ctx.d.accommodation.length],
  idcards: ['ID Cards & HMO', 'id-card', IdCards],
};
